import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Lock, Download, QrCode, Monitor, Apple, Smartphone, UserX, ShieldCheck, AlertTriangle } from 'lucide-react';
import { apiService, VpnPeer } from '../../services/apiService';

const WIREGUARD_LINKS = {
  windows: 'https://download.wireguard.com/windows-client/wireguard-installer.exe',
  macos: 'https://apps.apple.com/us/app/wireguard/id1451685025',
  ios: 'https://apps.apple.com/us/app/wireguard/id1441195209',
  android: 'https://play.google.com/store/apps/details?id=com.wireguard.android',
};

const detectOS = (): keyof typeof WIREGUARD_LINKS | null => {
  const ua = window.navigator.userAgent;
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macos';
  if (/Windows/i.test(ua)) return 'windows';
  return null;
};

const formatRelativeTime = (isoDate: string): string => {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHour / 24);
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
};

/**
 * Whether this customer's ACCOUNT has the Managed VPN service. That -- not
 * whether this particular person has a VPN user yet -- is what decides if the
 * ITP VPN Service entry appears in Apps. Someone on an account with the service
 * but no user of their own still gets the entry, and the modal tells them so.
 */
export const useVpnStatus = () => {
  const [peer, setPeer] = useState<VpnPeer | null>(null);
  const [available, setAvailable] = useState(false);
  const [installerAvailable, setInstallerAvailable] = useState(false);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const status = await apiService.getVpnStatus();
        if (status?.service_active === undefined) {
          // Hiding the entry is the right call for a customer, but a malformed
          // payload looks exactly like "no VPN service" -- say so in the console
          // so this isn't silently invisible again.
          console.warn('Unexpected VPN status payload, hiding VPN app:', status);
          return;
        }
        if (status.service_active) {
          setAvailable(true);
          setPeer(status.peer && status.peer.enabled ? status.peer : null);
          // Defaults to false when absent, so an older backend simply keeps
          // today's flow rather than advertising a button that 404s.
          setInstallerAvailable(status.installer_available === true);
        }
      } catch (err) {
        // No VPN service, or the endpoint is unreachable: just don't offer it.
        console.error('Failed to load VPN status:', err);
      }
    };
    loadStatus();
  }, []);

  return { peer, available, installerAvailable };
};

interface VpnModalProps {
  /** Null when this person has no VPN user yet, or theirs is disabled. */
  peer: VpnPeer | null;
  /**
   * Whether the backend can produce a code-signed Windows installer. When
   * false the installer button is not rendered at all and the existing
   * download-the-config flow is left exactly as it was.
   */
  installerAvailable?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const VpnModal: React.FC<VpnModalProps> = ({ peer, installerAvailable = false, isOpen, onClose }) => {
  const [showQr, setShowQr] = useState(false);
  const [qrValue, setQrValue] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [buildingInstaller, setBuildingInstaller] = useState(false);
  const [installerError, setInstallerError] = useState<string | null>(null);
  // Off by default. WireGuard has no separate auto-start setting: a tunnel
  // that is on when you shut down comes back at boot, so ticking this is the
  // difference between an always-on VPN and one you switch on when you need
  // it. That is the customer's call to make, not a default to inherit.
  const [autostart, setAutostart] = useState(false);

  const detectedOS = detectOS();
  const isPhone = detectedOS === 'ios' || detectedOS === 'android';
  // Windows only. macOS is deliberately out of scope: the App Store WireGuard
  // client is sandboxed, so a one-click installer there means shipping our own
  // notarized app -- a much bigger lift for far fewer users.
  const showInstaller = detectedOS === 'windows' && installerAvailable;

  if (!isOpen) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { filename, content } = await apiService.getVpnConfig();
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download VPN config:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleInstallerDownload = async () => {
    setBuildingInstaller(true);
    setInstallerError(null);
    try {
      const { filename, blob } = await apiService.getVpnInstaller(autostart);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download VPN installer:', err);
      // The config download below still works, so point at it rather than
      // leaving the person staring at a dead button.
      setInstallerError(
        "Couldn't build your installer just now. Use \u201cAdvanced: download config file\u201d below, or contact ITP support.",
      );
    } finally {
      setBuildingInstaller(false);
    }
  };

  const handleToggleQr = async () => {
    if (showQr) {
      setShowQr(false);
      return;
    }
    setShowQr(true);
    if (!qrValue) {
      setQrLoading(true);
      try {
        const { content } = await apiService.getVpnConfig();
        setQrValue(content);
      } catch (err) {
        console.error('Failed to load VPN config for QR code:', err);
        setShowQr(false);
      } finally {
        setQrLoading(false);
      }
    }
  };

  const osButton = (
    os: keyof typeof WIREGUARD_LINKS,
    label: string,
    Icon: React.ComponentType<{ className?: string }>,
  ) => (
    <a
      href={WIREGUARD_LINKS[os]}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
        detectedOS === os
          ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </a>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Lock className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">ITP VPN Service</h2>
              <p className="text-sm text-gray-600">Secure remote access to your office network</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!peer ? (
          <div className="p-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <UserX className="h-8 w-8 text-gray-400 mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-900 mb-1">
                You don't have a VPN user yet
              </p>
              <p className="text-sm text-gray-600">
                Your company has the ITP VPN service, but nobody has set up a connection for
                you. Ask whoever manages IT at your company, or contact ITP support, and we'll
                add you.
              </p>
            </div>
          </div>
        ) : (
        <div className="p-6">
          {showInstaller ? (
            /* Windows: one signed installer does the whole job -- installs
               WireGuard and adds this person's tunnel. Whether it also turns the
               tunnel on (and therefore keeps it on across reboots) is the
               autostart tickbox, and it is off unless asked for. The old
               three-step import flow stays available underneath for anyone who
               wants it, but it is no longer the thing we lead with. */
            <div>
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                      One-click setup for Windows
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Download the installer, double-click it, and approve the Windows
                      permission prompt. It installs everything you need and adds your
                      connection.
                    </p>
                    <label className="mt-3 flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autostart}
                        onChange={(e) => setAutostart(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-sm text-gray-600">
                        Connect straight away and stay connected
                        <span className="block text-xs text-gray-500">
                          Turns the VPN on at the end of setup and reconnects it every time
                          Windows starts. Leave this unticked to switch it on yourself, only
                          when you need it.
                        </span>
                      </span>
                    </label>
                    <button
                      onClick={handleInstallerDownload}
                      disabled={buildingInstaller}
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60 transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      {buildingInstaller ? 'Preparing your installer...' : 'Download ITP VPN installer'}
                    </button>
                    <p className="mt-3 text-xs text-gray-500">
                      This installer is yours personally &mdash; it contains your own VPN key.
                      Please don&rsquo;t share it or forward it to a colleague; ask us and
                      we&rsquo;ll set them up with their own.
                    </p>
                  </div>
                </div>
              </div>

              {installerError && (
                <div className="mt-3 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{installerError}</span>
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-gray-100">
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  How to connect on this computer
                </p>
                <ol className="text-sm text-gray-600 space-y-1.5 list-decimal list-inside">
                  <li>Download the installer using the button above.</li>
                  <li>Open it from your Downloads folder and click <strong>Yes</strong> when Windows asks for permission.</li>
                  <li>
                    {autostart
                      ? 'Follow the two screens. That\u2019s it \u2014 you\u2019re connected.'
                      : 'Follow the two screens, then click the WireGuard icon next to the clock and hit Activate whenever you want the VPN on.'}
                  </li>
                </ol>
                <p className="mt-3 text-xs text-gray-500">
                  {autostart
                    ? 'You only do this once. After that it reconnects on its own, and you can switch it off any time from the WireGuard icon next to the clock.'
                    : 'You only do this once. The VPN stays off until you switch it on, and nothing runs in the background in the meantime.'}
                </p>
              </div>

              <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-4">
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-60"
                >
                  {downloading ? 'Downloading...' : 'Advanced: download config file'}
                </button>
                <a
                  href={WIREGUARD_LINKS.windows}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  Advanced: WireGuard app on its own
                </a>
              </div>
            </div>
          ) : (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-3">Step 1 — Get the app</p>
              <div className="flex flex-wrap gap-2">
                {osButton('windows', 'Windows', Monitor)}
                {osButton('macos', 'macOS', Apple)}
                {osButton('ios', 'iOS', Smartphone)}
                {osButton('android', 'Android', Smartphone)}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-900 mb-3">Step 2 — Add your connection</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  {downloading ? 'Downloading...' : 'Download my VPN config'}
                </button>
                <button
                  onClick={handleToggleQr}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  <QrCode className="h-4 w-4" />
                  {showQr ? 'Hide QR code' : 'Show QR code'}
                </button>
              </div>

              {showQr && (
                <div className="mt-4 flex flex-col items-center bg-gray-50 border border-gray-200 rounded-lg p-4">
                  {qrLoading ? (
                    <p className="text-sm text-gray-500">Loading QR code...</p>
                  ) : qrValue ? (
                    <>
                      <QRCodeSVG value={qrValue} size={192} />
                      <p className="text-xs text-gray-500 mt-3 text-center">
                        In the WireGuard app, tap "+" then "Create from QR code" and scan this.
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-red-600">Couldn't load the QR code. Try again.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Lead with the instructions for the device they're actually on.
              Showing every platform's steps at once is how "3 short steps"
              reads as a wall of text. */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-sm font-semibold text-gray-900 mb-2">
              {isPhone ? 'How to connect on this phone' : 'How to connect on this computer'}
            </p>
            <ol className="text-sm text-gray-600 space-y-1.5 list-decimal list-inside">
              {isPhone ? (
                <>
                  <li>Install the WireGuard app using the button above.</li>
                  <li>Open it and tap the <strong>+</strong> button.</li>
                  <li>Choose <strong>Create from QR code</strong> and scan the code above.</li>
                  <li>Switch the new tunnel on.</li>
                </>
              ) : (
                <>
                  <li>Install WireGuard using the button above, then open it.</li>
                  <li>Click <strong>Import tunnel(s) from file</strong>.</li>
                  <li>Pick the config file you downloaded (check your Downloads folder).</li>
                  <li>Click <strong>Activate</strong>.</li>
                </>
              )}
            </ol>
            <p className="mt-3 text-xs text-gray-500">
              You only do this once. After that it's a single switch to connect.
            </p>
          </div>

          </>
          )}

          <div className="mt-4 text-xs text-gray-500">
            {peer.last_handshake
              ? `Last connected: ${formatRelativeTime(peer.last_handshake)}`
              : 'Not connected yet'}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default VpnModal;
